// PathfinderTab — self-contained campus pathfinding UI on Google Maps.
//
// Drop-in usage (see README.md in this folder):
//   <PathfinderTab apiKey={KEY} dataBaseUrl="/map-data/" />
// or with a custom data source (UMapsTools dev tab does this):
//   <PathfinderTab apiKey={KEY} dataSource={{ listBuildings, loadBuilding, loadNav }} />
//
// Everything under src/pathfinder/ is copy-out clean: no imports from the
// host app besides react and @react-google-maps/api.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { buildMapData, getRoomLocation, roomLabel, attachRoomInfo, attachRoomNames, attachParking, attachTransitStops, attachBuildingInterior } from './lib/graph.js';
import { findRoute } from './lib/route.js';
import { buildDirections } from './lib/directions.js';
import { pointInPolygon, nearestNode, destinationPoint, bearingDeg, haversineMeters, fitPanoHeadingFov } from './lib/geo.js';
import { createHttpDataSource, loadAllMapFiles } from './lib/dataSource.js';
import { buildCourseIndex } from './lib/courses.js';
import SearchPanel from './components/SearchPanel.jsx';
import DirectionsSteps from './components/DirectionsSteps.jsx';
import SupportPopup from './components/SupportPopup.jsx';
import ReportPopup from './components/ReportPopup.jsx';
import PanoramaViewer from './components/PanoramaViewer.jsx';
import { useRouteLayer } from './useRouteLayer.js';
import PlacesPanel from './components/PlacesPanel.jsx';
import ParkingLotCard from './components/ParkingLotCard.jsx';
import BikeParkingCard from './components/BikeParkingCard.jsx';
import EventSheet from './components/EventSheet.jsx';
import EventCalendar from './components/EventCalendar.jsx';
import { eventDestination, eventPosition, pinnableEvents } from './lib/events.js';
import { NO_FILTERS, attachPlaces, floorTag, mergePlacePhotos, nearbyPlaces, placeIconName, placeKindKey, placeTitle } from './lib/places.js';
import { pinDepartures, stopKeyOf, transitStopPlaces } from './lib/transit.js';
import { parkingLots as buildParkingLots } from './lib/parking.js';
import { bikeParking } from './lib/bikes.js';
import { DETAIL_ZOOM } from './lib/zoom.js';
import './pathfinder.css';

const ROUTE_OVERVIEW_HOLD_MS = 2400; // whole-route view holds this long before flying to the start
const FEEDBACK_KEY = 'umaps-pf-feedback'; // one-time first-visit welcome gate

// ── Bus times on the pins ────────────────────────────────────────────────────
// At this zoom and closer, a stop's pin prints the next two departures beside
// it. The label grows in two steps as the map comes in, because its WIDTH is
// what decides whether the next stop along is still readable:
//
//   17–18   compact — "F8  19m", ~81 px wide
//   19      full    — "F8  19m (9:23)", ~119 px
//   past 19 the same, drawn at 1.45x
//
// Measured against the real stop positions: the pairs facing each other across
// a road (Dafoe at Agriculture, Dafoe at University, Freedman at King's) sit
// 43 px apart at zoom 17, 86 px at 18 and 172 px at 19. So the compact label
// clears them from 18 and the full one from 19 — and at 17 those three pairs
// do overlap, accepted deliberately because a countdown you can see while
// scanning the whole campus is worth more than three tidy pairs, and one step
// in fixes it. Re-run that arithmetic before making any of these wider.
// Shared with the parking lots, which reveal their full name and status at the
// same moment — see lib/zoom.js.
const BUS_LABEL_MIN_ZOOM = DETAIL_ZOOM;
const BUS_LABEL_CLOCK_ZOOM = 19;
// Past this the pin and its times are drawn larger: this is the zoom somebody
// is at when they have stopped scanning the map and started reading one stop,
// which is when 9.5px type at arm's length stops being enough. A vector map
// zooms fractionally, so 19.4 counts as past 19.
const BUS_LABEL_BIG_ZOOM = 19;
const BUS_LABEL_BIG_SCALE = 1.45;
// How long a stop's fetched departures are reused before asking again. The
// server caches 20 s and rate-limits 60 requests a minute per IP, so with the
// handful of stops that fit on screen at this zoom this stays an order of
// magnitude inside the budget — while still being fresh enough that a "6m" is
// never more than a minute and a half old.
const BUS_TIMES_TTL_MS = 90 * 1000;
// A stop that failed is left alone far longer. The likeliest failures are the
// server having no API key at all and the rate limiter saying no, and both are
// made worse by asking again immediately.
const BUS_TIMES_ERROR_TTL_MS = 5 * 60 * 1000;
// Most a single pass will ask for, however many stops are in frame.
const BUS_TIMES_MAX_PER_PASS = 8;
// Re-derive the labels (and top up what has expired) on this beat. The minutes
// have to count down while somebody stands there reading them.
const BUS_TICK_MS = 30 * 1000;

// How often a parking lot's badge is re-derived. Its answer only changes twice
// a day (7:30 and 4:30), but it changes for everybody at once and the page is
// the kind that sits open on a phone all afternoon — so it is checked on the
// minute rather than left saying "$2.50" half an hour after the lot went free.
const PARKING_TICK_MS = 60 * 1000;

const MY_LOCATION_MAX_METERS = 1000; // farthest nav node a GPS-based From will snap to
const MYLOC_LABEL = '📍 My location'; // legacy From text meaning "route from my GPS"
// Non-building pseudo-slugs whose nodes shouldn't be offered as a From building.
const PSEUDO_SLUGS = new Set(['outdoors', 'tunnels', 'building_connections']);

// Why a settled search found nothing — the same distinction the status line
// draws, as a stable key a host can count. Mirrors `status` below; keep the two
// in step.
function failureReason(o, fromIds, toIds) {
  if (fromIds.length && toIds.length && fromIds.every(id => toIds.includes(id))) return 'same-place';
  if (o.accessible && o.noElevators) return 'accessible-no-elevators';
  if (o.accessible) return 'no-accessible-route';
  return 'no-route';
}

// Rough "Mobile · iOS Safari" style label for problem reports.
function deviceLabel() {
  const ua = navigator.userAgent || '';
  const kind = /Mobile|Android|iPhone|iPad|iPod/.test(ua) ? 'Mobile' : 'Desktop';
  const os = /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Windows/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux' : '';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /CriOS|Chrome/.test(ua) ? 'Chrome'
    : /FxiOS|Firefox/.test(ua) ? 'Firefox'
    : /Safari/.test(ua) ? 'Safari' : '';
  return [kind, [os, browser].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
}

// `tuning` (optional): partial cost-model override passed to findRoute as
// options.costs, plus `walkSpeed` (m/min) for the time estimate — the
// Pathfinder Tuner drives this; the production app just omits it.
//
// External-drive props: when `driveRoute = {fromId, toId}` is set the tab routes
// those node ids directly (ignoring the search UI) using `driveOptions` (falls
// back to internal toggles); `hidePanel` hides the search panel for a clean map
// (random-path + blind rating modes); `onMapData(md)` hands the merged map model
// back so the driver can pick random endpoints.
//
// `driveFromLabel` / `driveToLabel` (optional) name the two ends on the map
// pills. Without them a driven route reads "Start" and "End", which is fine when
// the user typed the endpoints and can see them in the search boxes — and no use
// at all when a host chose both on their behalf (the events page picks a parking
// lot; the pill is the only place that says which one).
//
// `onPlaceLog(event)` (optional): one interaction with the Nearby panel —
// `{ event: 'open'|'kind'|'view'|'go'|'filter'|'empty', kind?, placeId?,
// placeName?, building?, filter?, count? }`. Fire-and-forget; the panel never
// waits on it. Omit the prop and nothing is emitted.
//
// `onScanLog(scan)` (optional): a printed sign was scanned and resolved to a
// node — `{ code, nodeId, building, floor }`. Fires ONCE per arrival, before any
// destination is chosen, precisely so the scans that never become a search are
// still counted: those are the badly placed signs. Fire-and-forget.
//
// `onPhotoSubmit(submission)` (optional, async): a visitor offering photos of a
// place that has none — `{ placeId, placeName, placeType, building, roomNumber,
// floor, images: [jpegDataUrl] }`. Where they go, and who approves them, is the
// host's business; omit the prop and the panel stops asking. The ones a host
// has accepted come back through the data source's optional `loadPlacePhotos()`
// → `{ [placeId]: [url, …] }`, merged onto each place's own images.
export default function PathfinderTab({
  apiKey, mapId = null, dataSource, dataBaseUrl, defaultCenter, defaultZoom = 17, tuning = null,
  driveRoute = null, driveOptions = null, hidePanel = false, onMapData = null,
  driveFromLabel = '', driveToLabel = '',
  enableFeedback = true, onRouteLog = null, onEmailCollect = null,
  onReport = null, onPhotoSubmit = null, onPlaceRate = null, onPlaceLog = null,
  onScanLog = null, notice = null,
  // ── Club events ───────────────────────────────────────────────────────────
  // Host-owned, because everything about an event except *where* it is belongs
  // to the host: it fetches them, it decides what "when" reads as, and it
  // renders the club's authored body. This folder places them on the map,
  // draws the pins and the sheet, and routes to them. See lib/events.js for
  // the shape.
  events = null,
  renderEventBody = null,   // (event) => node — the club's blocks, host-rendered
  // The way in for the people who MAKE events: `{ href, label }`, drawn in the
  // calendar's top-right corner. The host's, because where its clubs sign in is
  // the host's own page. Omit it and the corner stays empty.
  clubLogin = null,
  // The "get the app" button, in the bottom-centre slot the Nearby button used
  // to hold. A prop rather than markup in here because the app being advertised
  // is the host's: { href, icon, title, sub }.
  appPromo = null,
  // The same slot, drawn by the host: `() => node`. Wins over `appPromo` when
  // given — UMapsWeb puts the two store badges here, and the store marks are
  // host assets that do not belong in this folder. This folder still decides
  // WHEN the slot shows; the host only decides what is in it.
  renderAppPromo = null,
  // The route's QR code, drawn by the host: `({ url, size }) => node`. A prop
  // for the same reason `appPromo` is one — the UMaps mark that belongs in the
  // middle of it is a host asset, and no logo lives inside this folder. Left
  // out, `QrPopup` falls back to a public generator with no mark on it.
  renderQr = null,
  // The ask on the first visit: `{ title, blurb, href }`. A prop for the same
  // reason `appPromo` is one — whose project this is, and where its money goes,
  // are the host's facts and would follow the map into anybody else's app.
  //
  // Giving it a `blurb` REPLACES the welcome popup with this ask, so the first
  // thing a visitor is asked is either one or the other and never both. Give
  // only `href` and the welcome popup stays as it was, with a donate button
  // added to it.
  support = null,
}) {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey || '' });

  // ── Map data ───────────────────────────────────────────────────────────────
  const [mapData, setMapData] = useState(null);
  const [dataError, setDataError] = useState(null);
  // The parking file as authored, for the lot layer — see the load effect.
  const [parkingRaw, setParkingRaw] = useState(null);
  // Star ratings live beside the places rather than on them: a rating changes
  // while the app is open, and the merged map model is rebuilt only on load.
  const [placeRatings, setPlaceRatings] = useState({});
  const ds = useMemo(
    () => dataSource || (dataBaseUrl ? createHttpDataSource(dataBaseUrl) : null),
    [dataSource, dataBaseUrl]
  );
  useEffect(() => {
    if (!ds) { setDataError('No dataSource or dataBaseUrl provided.'); return; }
    let cancelled = false;
    Promise.all([
      loadAllMapFiles(ds),
      ds.loadRoomInfo ? ds.loadRoomInfo() : null,
      ds.loadParking ? ds.loadParking() : null,
      ds.loadRoomNames ? ds.loadRoomNames() : null,
      ds.loadPlaces ? ds.loadPlaces() : null,
      // Student photos the host has accepted for a place, keyed by place id.
      // Optional and non-fatal: without it places just show their own images.
      ds.loadPlacePhotos ? ds.loadPlacePhotos().catch(() => null) : null,
      // Star ratings, same deal: absent just means every place reads "No rating".
      ds.loadPlaceRatings ? ds.loadPlaceRatings().catch(() => null) : null,
      // The campus bus stops. Optional and non-fatal: without the file the Bus
      // stops tab simply never appears.
      ds.loadTransitStops ? ds.loadTransitStops().catch(() => null) : null,
    ])
      .then(([files, roomInfo, parking, roomNames, places, placePhotos, placeRatings, transitStops]) => {
        if (cancelled) return;
        const md = buildMapData(files);
        if (roomInfo?.rooms) attachRoomInfo(md, roomInfo.rooms); // annotates roomIndex with type/capacity
        if (roomNames) attachRoomNames(md, roomNames);           // friendly room names from the RoomNames sheets
        if (parking) attachParking(md, parking);                 // parking-lot polygons (floor 0)
        // The same file again, kept whole this time. attachParking takes the
        // shapes; the lot layer needs what the shapes MEAN — the category and
        // the schedule that decide a lot's colour and what it costs right now —
        // and both live in the one bundled file, so there is nothing to fetch.
        setParkingRaw(parking || null);
        // Bus stops arrive as their own file, not in places.json — Winnipeg
        // Transit owns them — but they are the same kind of question to
        // somebody on campus, so they join the places list before it is
        // attached and get pins, distance sorting and a detail sheet for free.
        const busPlaces = transitStopPlaces(transitStops);
        const allPlaces = [...(places || []), ...busPlaces];
        if (allPlaces.length) attachPlaces(md, mergePlacePhotos(allPlaces, placePhotos));
        // Searchable too, but only the ones that can actually be walked to: a
        // search result is something you pick and then get routed to.
        if (busPlaces.length) attachTransitStops(md, busPlaces);
        setPlaceRatings(placeRatings || {});
        setMapData(md);
        onMapData?.(md);
      })
      .catch(err => { if (!cancelled) setDataError(String(err.message || err)); });
    return () => { cancelled = true; };
  // onMapData is a stable host callback fired once per load; re-running on its
  // identity would refetch all map files needlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ds]);

  // ── Map bootstrap ──────────────────────────────────────────────────────────
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const coneRef = useRef(null); // view cone for the open panorama
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter || { lat: 0, lng: 0 },
      zoom: defaultZoom,
      mapTypeId: 'satellite',
      tilt: 0, heading: 0,
      // A VECTOR map (needs a Map ID) unlocks free rotation/tilt — two-finger
      // twist on mobile, Ctrl+drag on desktop. Without a Map ID the map is
      // raster and stays north-up (Google raster maps cannot rotate).
      ...(mapId
        ? { mapId, rotateControl: true, headingInteractionEnabled: true, tiltInteractionEnabled: true }
        : { rotateControl: false }),
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      clickableIcons: false, gestureHandling: 'greedy',
    });
    setMapReady(true);
  }, [isLoaded, defaultCenter, defaultZoom, mapId]);

  // Initial view: fit all building outlines (unless the host pinned a center).
  const didFitRef = useRef(false);
  useEffect(() => {
    if (!mapReady || !mapData || defaultCenter || didFitRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    let any = false;
    for (const b of mapData.buildings)
      for (const polys of b.polygonsByFloor.values())
        for (const p of polys) { p.ring.forEach(pt => bounds.extend(pt)); any = true; }
    if (!any) for (const n of mapData.nodes.values()) { bounds.extend({ lat: n.lat, lng: n.lng }); any = true; }
    if (any) { mapInstanceRef.current.fitBounds(bounds, 60); didFitRef.current = true; }
  }, [mapReady, mapData, defaultCenter]);

  // ── One-time welcome prompt (first visit) ─────────────────────────────────
  // Asks the visitor to follow us on Instagram or leave a UManitoba email for
  // updates. Shown once per browser; a stored record (email left or dismissed)
  // suppresses it thereafter. The email is handed to the host via
  // onEmailCollect and stored on a SEPARATE list — never bundled with the route
  // logs.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false); // "?" support/email modal (screen-centered)
  const [reportOpen, setReportOpen] = useState(false);   // "!" report-a-problem modal
  useEffect(() => {
    if (!enableFeedback) return;
    try { if (!localStorage.getItem(FEEDBACK_KEY)) setFeedbackOpen(true); } catch { /* ignore */ }
  }, [enableFeedback]);

  const dismissFeedback = useCallback(() => {
    setFeedbackOpen(false);
    try { if (!localStorage.getItem(FEEDBACK_KEY)) localStorage.setItem(FEEDBACK_KEY, JSON.stringify({ answered: false, ts: Date.now() })); } catch { /* ignore */ }
  }, []);
  // Email left on the welcome popup: remember it so the prompt stays gone, and
  // pass it to the host's collector. The popup keeps itself open to show the
  // thank-you, so this only records — it doesn't close.
  const submitWelcomeEmail = useCallback((email) => {
    try { localStorage.setItem(FEEDBACK_KEY, JSON.stringify({ answered: true, ts: Date.now() })); } catch { /* ignore */ }
    onEmailCollect?.(email);
  }, [onEmailCollect]);

  // ── Geolocation → detected building ───────────────────────────────────────
  const [userPos, setUserPos] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watch = navigator.geolocation.watchPosition(
      p => setUserPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watch);
  }, []);

  const detectedBuilding = useMemo(() => {
    if (!mapData || !userPos) return null;
    for (const b of mapData.buildings)
      for (const ring of b.outlines)
        if (pointInPolygon(userPos, ring)) return b.slug;
    return null;
  }, [mapData, userPos]);

  const detectedName = useMemo(() => (
    detectedBuilding ? (mapData?.buildings.find(b => b.slug === detectedBuilding)?.name || detectedBuilding) : null
  ), [detectedBuilding, mapData]);

  // When outside any building but near campus, pre-fill From with the building
  // that owns the nearest nav node to the user, so they only type the room #.
  const nearestBuildingName = useMemo(() => {
    if (!mapData || !userPos || detectedBuilding) return null;
    const hit = nearestNode([...mapData.nodes.values()], userPos, {
      maxMeters: 1500,
      filter: n => n.building && !PSEUDO_SLUGS.has(n.building),
    });
    if (!hit) return null;
    return mapData.buildings.find(b => b.slug === hit.node.building)?.name || null;
  }, [mapData, userPos, detectedBuilding]);

  // ── Search / select / routing state ────────────────────────────────────────
  const [selectedRoom, setSelectedRoom] = useState(null); // roomIndex entry (destination)
  // A shared link captured on first load. Two flavours, both craftable by hand:
  //   ?b=<building>&r=<room>[&from=<text>]  → a full route (opens the preview)
  //   ?from=<text>                          → only the START is preset; the
  //       recipient just picks their destination and toggles. `from` text is
  //       resolved the same way the From box is ("Building Room", e.g.
  //       ?from=Armes 200), so it needn't know internal slugs.
  //
  // `&a=` and `&t=` carry the two ROUTING TOGGLES as their own tri-states
  // (a: 0 any / 1 accessible / 2 stairs only, t: 0 any / 1 prefer tunnels /
  // 2 avoid tunnels). ⚠️ Without them a link names a destination but not the
  // route, and the two differ: the same From→To with the accessible toggle on
  // is a different path through a different set of shafts. That is exactly the
  // cut /admin reopens a bad rating on — "accessible · bad" is the number the
  // ratings tab leads with — so a link that dropped the toggles would reopen a
  // route nobody complained about and show it working.
  const [pendingLink, setPendingLink] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const b = p.get('b'), r = p.get('r'), from = p.get('from') || '';
      const place = p.get('place');
      const nodeId = p.get('n') || '';
      // Anything that is not one of the three states is no preference, so a
      // truncated or hand-mangled link opens the ordinary map rather than
      // silently applying half a set of options.
      const mode = (v) => (v === '1' ? 1 : v === '2' ? 2 : 0);
      const a = mode(p.get('a')), t = mode(p.get('t'));
      // The sign's printed identifier ("E1-F1A"). Display only — it never picks
      // the node, `n` does — so it is format-checked to the shape the sign
      // generator emits rather than trusted, and anything else is dropped. That
      // keeps arbitrary text out of the From box, which is a field the user
      // reads and reasons about.
      const sign = /^[A-Z0-9-]{1,24}$/i.test(p.get('s') || '') ? p.get('s').toUpperCase() : '';
      if ((b && r) || from || place || nodeId) return { b, r, from, place, n: nodeId, s: sign, a, t };
    } catch { /* ignore */ }
    return null;
  });
  const [routing, setRouting] = useState(false);
  const [fromCommitted, setFromCommitted] = useState(false); // user tapped "Find Route" for a bare building / My location
  const [routeConfirmed, setRouteConfirmed] = useState(false); // true after user taps GO in preview pill
  const [fromText, setFromText] = useState('');           // editable From text ("Building Number")
  const fromAutoRef = useRef(true);                        // follow detected location until edited
  // Tri-state: 0=any, 1=accessible/elevators, 2=stairs only
  const [accessibleMode, setAccessibleMode] = useState(0);
  // Tri-state: 0=no preference, 1=prefer tunnels, 2=avoid tunnels (UI only for now)
  const [tunnelMode, setTunnelMode] = useState(0);
  const options = useMemo(() => ({
    accessible: accessibleMode === 1,
    noElevators: accessibleMode === 2,
    tunnels: tunnelMode === 1,
    noTunnels: tunnelMode === 2,
  }), [accessibleMode, tunnelMode]);
  const [activeLeg, setActiveLeg] = useState(0);
  const [dirOpen, setDirOpen] = useState(false); // step-by-step directions expanded
  // Which instruction is being looked at. Shared by the list and the map icons
  // so selecting in either place shows in both.
  const [activeStepId, setActiveStepId] = useState(null);
  const [pano, setPano] = useState(null); // { node, bearing } — open intersection panorama
  // Where the viewer is currently looking, as { bearing, fov }. Deliberately a
  // ref, NOT state: the viewer reports this on every frame of a drag or coast,
  // and re-rendering this component 60 times a second tore down the whole route
  // layer each frame — the chevron animation restarted before it could advance
  // and the "Look" pins and floor-switch overlays were destroyed and rebuilt,
  // which read as freezing and flashing. The cone is drawn imperatively instead.
  const panoViewRef = useRef(null);
  const [panoDiv, setPanoDiv] = useState(null);    // map-anchored container the viewer portals into

  // Auto-fill From: inside building → "Building " (append room); near campus →
  // "Nearest Building " (append room). Otherwise leave it blank so the user just
  // types the nearest room number they can see. Stops as soon as the user edits.
  useEffect(() => {
    if (!fromAutoRef.current || !mapData) return;
    if (detectedName) setFromText(`${detectedName} `);
    else if (nearestBuildingName) setFromText(`${nearestBuildingName} `);
  }, [detectedName, nearestBuildingName, mapData]);

  /**
   * A scanned sign, resolved to the node it stands at.
   *
   * `qrPin` is a NODE, not text — which is the whole point of a sign. Text would
   * have to be re-parsed through the From box, and the box cannot express "this
   * exact door": it resolves a room, and a room with four doors is four possible
   * starting points. Pinning the node means the route starts at the door the
   * student is literally standing in.
   *
   * Every way this can fail lands on the ordinary search screen with a sentence
   * saying why, never on a dead end: a sign is a physical object that outlives
   * the data behind it, and somebody is standing in a corridor holding a phone.
   */
  const [signPin, setSignPin] = useState(null);       // { nodeId, code, label } | null
  const [signNotice, setSignNotice] = useState('');   // why a scan didn't take
  // A scan is reported ONCE per arrival, keyed by the node it resolved to.
  // Without this guard React's double-invoked effects (StrictMode) and any
  // re-run of the effect would each count the same person walking in the door.
  const scanLoggedRef = useRef('');
  useEffect(() => {
    const nodeId = pendingLink?.n;
    if (!nodeId || !mapData) return;
    const node = mapData.nodes.get(String(nodeId));
    if (!node) {
      setSignNotice('That sign points somewhere the map no longer has. Search for where you are instead.');
      return;
    }
    fromAutoRef.current = false;                  // a scan beats geolocation
    const code = pendingLink?.s || '';
    // WHERE they are, in words a student recognises: the building, and the floor
    // if the sign is not on the ground one.
    //
    // The From box is deliberately left EMPTY. It used to be filled with the
    // sign's printed identifier, so the panel read "From E1-F1A" — which is a
    // label for us, not an address for them: it names a piece of plastic on a
    // wall, not a place, and sitting in the From box it looked like something
    // they were meant to have typed and might need to correct. The start is
    // already pinned to the exact node; the person scanning only has to say
    // where they are going.
    const bName = mapData.buildings.find(b => b.slug === node.building)?.name || '';
    const floor = node.floor;
    const place = [bName || node.label || 'this spot',
      Number.isFinite(floor) && floor !== 1 ? `Floor ${floor}` : '']
      .filter(Boolean).join(' · ');
    setSignPin({ nodeId: node.id, code, label: node.label || '', place });
    setFromCommitted(true);
    setSignNotice('');
    // Report the scan itself, not just the search it may lead to. A sign that is
    // scanned and then abandoned never produces a route log, and those are the
    // signs worth knowing about — badly placed, or pointing somewhere that makes
    // no sense to the person standing in front of it.
    if (scanLoggedRef.current !== node.id) {
      scanLoggedRef.current = node.id;
      onScanLog?.({ code, nodeId: node.id, building: node.building || null, floor: node.floor ?? null });
    }
  }, [pendingLink, mapData, onScanLog]);

  /**
   * The scanned sign as a PLACE on the map, not just a pinned start node.
   *
   * Scanning used to change the panel and leave the map exactly where it was —
   * pulled back over the whole campus, the building a blank footprint. So the
   * one thing a sign is uniquely able to answer, "where am I standing", was the
   * one thing the map did not show: the person had the answer in their hand and
   * still had to find themselves on it.
   *
   * This is what the rest of the map keys on, in the same shape as a selected
   * room or a previewed place: the building whose insides to draw, the FLOOR to
   * draw them at (a sign on the third floor over a ground-floor plan is worse
   * than no plan — every corridor is in the wrong place), and the exact point to
   * mark and fly to.
   */
  const scanLoc = useMemo(() => {
    if (!signPin || !mapData) return null;
    const n = mapData.nodes.get(signPin.nodeId);
    if (!n || n.lat == null || n.lng == null) return null;
    return {
      nodeId: n.id,
      building: n.building || null,
      floor: n.floor ?? null,
      position: { lat: n.lat, lng: n.lng },
      label: signPin.place || n.label || '',
    };
  }, [signPin, mapData]);

  // Apply a shared link once the map data is ready. A preset From always pins
  // the start (and locks out geolocation auto-fill); a b+r destination also
  // opens the route preview. A From-only link leaves the recipient on the
  // search screen with the start already filled — they just pick their "to".
  useEffect(() => {
    if (!pendingLink || !mapData) return;
    const { b, r, from } = pendingLink;
    // Applied before the destination, so the route is built once with the
    // options already set rather than being computed and then recomputed.
    if (pendingLink.a) setAccessibleMode(pendingLink.a);
    if (pendingLink.t) setTunnelMode(pendingLink.t);
    if (from) {
      fromAutoRef.current = false;             // don't let geolocation overwrite the shared From
      setFromText(from);
      setFromCommitted(true);
    }
    if (b && r) {
      const entry = mapData.roomIndex.find(
        e => e.building === b && String(e.roomNumber).toLowerCase() === r.toLowerCase()
      );
      if (entry) {
        fromAutoRef.current = false;
        setSelectedRoom(entry);
        setRouting(true);
      }
    }
    if (pendingLink.place) setPendingPlace(pendingLink.place);
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
    setPendingLink(null);
  }, [pendingLink, mapData]);

  // ── Study spots & libraries panel ──────────────────────────────────────────
  // A shared ?place=<id> link, held until the places are attached.
  const [pendingPlace, setPendingPlace] = useState(null);
  const [placesOpen, setPlacesOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  // The active tab lives here, not in the panel, because the map pins follow
  // it. There is no "All": bathrooms outnumber study spots two to one, so a
  // mixed list buries the thing most people opened this for.
  const [placeKind, setPlaceKind] = useState('study');
  const [placeFilters, setPlaceFilters] = useState(NO_FILTERS);

  // Bumped to ask the map to zoom out over the pins — on opening the list, and
  // on coming back to it from a place (which had zoomed right in).
  const [fitPinsKey, setFitPinsKey] = useState(0);

  const handlePlaceKind = useCallback((k) => {
    setPlaceKind(k);
    setFitPinsKey(n => n + 1);   // a tab is a deliberate act — refit to it
    onPlaceLog?.({ event: 'kind', kind: k });
  }, [onPlaceLog]);
  const handleClosePlaces = useCallback(() => { setPlacesOpen(false); setSelectedPlace(null); }, []);

  // ── Nearby ⇄ route flow are mutually exclusive ────────────────────────────
  // Both own the bottom of the screen (the sheet vs. the route pill and the
  // directions sheet) and both own the camera, so on a phone they cannot share
  // it. The one thing that still opens Nearby — a tapped bus-stop pin — drops
  // the route flow itself (`handleBusPin`); this is the other direction, and it
  // is an effect rather than a call bolted onto
  // each handler so that *every* route into routing is covered — the search
  // panel, a tapped room polygon, a shared ?b=&r= link, Directions from a place
  // — without any of them having to remember.
  useEffect(() => {
    if (routing || selectedRoom) handleClosePlaces();
  }, [routing, selectedRoom, handleClosePlaces]);
  const handleSelectPlace = useCallback((entry) => {
    setSelectedPlace(entry);
    if (!entry) setFitPinsKey(k => k + 1);   // back to the list → back to the overview
    else onPlaceLog?.({
      event: 'view', kind: entry.place.type, placeId: entry.place.id,
      placeName: placeTitle(entry.place), building: entry.place.building,
    });
  }, [onPlaceLog]);

  // A rating the host has stored: fold the average it hands back into the local
  // map so the list row behind the sheet updates too. The server's number is
  // taken as final rather than recomputed here — two people rating the same
  // spot at once would otherwise drift apart on screen.
  const handlePlaceRate = useCallback(async (placeId, stars, previous) => {
    const next = await onPlaceRate(placeId, stars, previous);
    if (next && Number.isFinite(next.avg)) {
      setPlaceRatings(prev => ({ ...prev, [placeId]: { avg: next.avg, count: next.count } }));
    }
    return next;
  }, [onPlaceRate]);

  // Pins for whatever the list is showing. Capped at the nearest handful when a
  // position is known: fitting all 60-odd would zoom out to the whole campus,
  // which is the opposite of "what's near me".
  const NEARBY_PINS = 12;
  const placePins = useMemo(() => {
    if (!placesOpen || routing || !mapData?.places?.length) return null;
    const ranked = nearbyPlaces(mapData.places, userPos, { kind: placeKind, filters: placeFilters });
    const shown = userPos ? ranked.slice(0, NEARBY_PINS) : ranked;
    return shown.map(e => ({
      id: e.place.id,
      position: e.loc.position,
      // Washrooms pin per type, so `kind` is the glyph name there; everything
      // else pins by its kind key, which is what splits amenities by category.
      kind: e.place.type === 'bathroom' ? placeIconName(e.place) : placeKindKey(e.place),
      title: placeTitle(e.place),
      // Which floor a washroom is on decides whether it is worth walking to,
      // and the pin was the one place that never said. Same for an amenity:
      // a microwave two floors up is a different proposition from one here.
      floorTag: ['bathroom', 'amenity'].includes(e.place.type) ? floorTag(e.loc.floor) : null,
    }));
  }, [placesOpen, routing, mapData, userPos, placeKind, placeFilters]);

  // Pulled back, the nearest handful stops being the useful answer — you want
  // to see where they all are — so at overview zoom the whole set of the
  // SELECTED kind replaces them. Only for study spots and libraries: 126
  // washrooms would carpet the campus, and which floor they are on matters too
  // much to read from an overview.
  //
  // Gated on the panel being open, like every other pin. These used to draw
  // from mapData alone, which put 69 markers on the map the moment the app
  // loaded, before anyone had asked for anything.
  const overviewPins = useMemo(() => {
    if (!placesOpen || routing || !mapData?.places?.length) return null;
    if (placeKind !== 'study' && placeKind !== 'library') return null;
    return mapData.places
      .filter(e => e.place.type === placeKind && e.loc?.position)
      .map(e => ({
        id: e.place.id,
        position: e.loc.position,
        kind: e.place.type,
        title: placeTitle(e.place),
      }));
  }, [placesOpen, routing, mapData, placeKind]);

  const handlePlacePin = useCallback((id) => {
    const entry = mapData?.places?.find(e => e.place.id === id);
    if (entry) setSelectedPlace(entry);
  }, [mapData]);

  // ── Bus stops on the resting map ──────────────────────────────────────────
  // The only kind of place drawn before anybody opens a panel. Everything else
  // here waits to be asked for, and should: a washroom is where it was
  // yesterday, so a pin sitting on the map all day tells you nothing new. A bus
  // stop is the opposite — the useful fact about it changes every few minutes,
  // and the person who wants it is standing on a corner deciding whether to run,
  // not typing a search. Drawing the twenty-odd of them is what turns that into
  // one tap.
  //
  // They go away the moment the screen belongs to something else — a route being
  // walked, the Nearby sheet (whose Bus stops tab draws its own pins, which
  // would otherwise double up), or a host driving the map — which is what keeps
  // "no route enabled" the exact condition for showing them.
  const busStops = useMemo(() => {
    if (placesOpen || routing || hidePanel || driveRoute) return null;
    if (!mapData?.places?.length) return null;
    return mapData.places
      .filter(e => e.place.type === 'busstop' && e.loc?.position)
      .map(e => ({
        id: e.place.id,
        position: e.loc.position,
        kind: 'busstop',
        title: placeTitle(e.place),
        stopKey: stopKeyOf(e.place),
      }));
  }, [placesOpen, routing, hidePanel, driveRoute, mapData]);

  // ── The next two buses, printed on the pin ────────────────────────────────
  //
  // Close in, "when is the next one" stops being a question worth a tap: the
  // person is at the kerb deciding whether to run, and a pin that says "F8 6m /
  // 889 10m" has already answered them. Pulled back it is twenty-one boxes of
  // text over each other, so the labels exist only above BUS_LABEL_MIN_ZOOM —
  // which is also what keeps this affordable, because every label is somebody
  // else's API quota.
  //
  // Two rules do the rationing, and both matter. Only stops actually IN FRAME
  // are asked about — panned across campus that is a handful, never all
  // twenty-one — and each answer is reused for BUS_TIMES_TTL_MS, so the beat
  // below re-derives the countdown from data it already has and only tops up
  // what has genuinely gone stale. A stop nobody has zoomed in on is never
  // fetched at all.
  const [busTimes, setBusTimes] = useState({});     // stopKey → { departures }
  // How close the map is: 0 = no labels, 1 = compact, 2 = with clock times,
  // 3 = the same drawn larger.
  // A tier rather than the zoom itself: a vector map reports fractional zoom all
  // the way through a pinch, and storing that would re-render this component on
  // every frame of one to redraw pins that had not changed.
  const [busTier, setBusTier] = useState(0);
  const [busNow, setBusNow] = useState(() => Date.now()); // the beat, so "6m" counts down
  // stopKey → the earliest time we may ask about it again. Doubles as the
  // in-flight guard: it is written before the request goes out, so a second
  // pass while the first is still in the air asks for nothing.
  const busReqRef = useRef(new Map());

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map || !busStops?.length) { setBusTier(0); return undefined; }
    const gmaps = window.google.maps;
    const load = ds?.loadDepartures;

    const pass = () => {
      const z = map.getZoom();
      const tier = z == null ? 0
        : z > BUS_LABEL_BIG_ZOOM ? 3
        : z >= BUS_LABEL_CLOCK_ZOOM ? 2
        : z >= BUS_LABEL_MIN_ZOOM ? 1
        : 0;
      // Same value re-set is free — React bails out — so this costs nothing on
      // the pans and pinches that don't cross a threshold.
      setBusTier(tier);
      if (!tier || !load || document.hidden) return;
      const bounds = map.getBounds();
      if (!bounds) return;
      const now = Date.now();
      const due = busStops
        .filter(s => s.stopKey
          && bounds.contains(s.position)
          && (busReqRef.current.get(s.stopKey) || 0) <= now)
        .slice(0, BUS_TIMES_MAX_PER_PASS);
      for (const s of due) {
        busReqRef.current.set(s.stopKey, now + BUS_TIMES_TTL_MS);
        load(s.stopKey)
          .then(d => setBusTimes(prev => ({ ...prev, [s.stopKey]: { departures: d?.departures || [] } })))
          // Nothing is shown for a stop we couldn't reach: a pin with no label
          // is the pin as it has always been, which is the right amount of
          // wrong to be about a bus.
          .catch(() => busReqRef.current.set(s.stopKey, Date.now() + BUS_TIMES_ERROR_TTL_MS));
      }
    };

    pass();
    const listener = gmaps.event.addListener(map, 'idle', pass);
    const timer = setInterval(() => { setBusNow(Date.now()); pass(); }, BUS_TICK_MS);
    return () => { gmaps.event.removeListener(listener); clearInterval(timer); };
  }, [mapReady, busStops, ds, mapInstanceRef]);

  // ── Parking lots ──────────────────────────────────────────────────────────
  // Drawn from the bundled file, with each lot's badge derived against the
  // clock — see lib/parking.js for why the hour is the whole answer. The tick
  // is what keeps "Open to all" from appearing half an hour late on a page that
  // has been open since lunch; the layer reconciles by id, so a minute where
  // nothing crossed a boundary costs no DOM work.
  const [parkingNow, setParkingNow] = useState(() => Date.now());
  useEffect(() => {
    if (!parkingRaw) return undefined;
    const t = setInterval(() => setParkingNow(Date.now()), PARKING_TICK_MS);
    return () => clearInterval(t);
  }, [parkingRaw]);

  const parkingLots = useMemo(
    () => (parkingRaw ? buildParkingLots(parkingRaw, new Date(parkingNow)) : null),
    [parkingRaw, parkingNow],
  );

  // ── Bike parking ──────────────────────────────────────────────────────────
  // Read out of the graph rather than from a file: the tool files these as
  // outdoor nodes typed `parking` and tells them apart by their label alone
  // (see lib/bikes.js). Drawn on the resting map with the bus stops, under the
  // same rule and put away by the same things — a route, the Nearby sheet, or a
  // driven host. Unlike a bus stop, a rack IS on the walking network, so its
  // card can offer Directions.
  const bikeSpots = useMemo(() => (mapData ? bikeParking(mapData) : []), [mapData]);
  const bikePins = useMemo(() => {
    if (placesOpen || routing || hidePanel || driveRoute) return null;
    return bikeSpots.length ? bikeSpots : null;
  }, [bikeSpots, placesOpen, routing, hidePanel, driveRoute]);

  const [selectedBikeId, setSelectedBikeId] = useState(null);
  const selectedBike = useMemo(
    () => bikeSpots.find(s => s.id === selectedBikeId) || null,
    [bikeSpots, selectedBikeId],
  );
  const handleBikePin = useCallback((id) => {
    setSelectedRoom(null);
    setSelectedLotId(null);
    setSelectedBikeId(id);
  }, []);

  // Riding to a rack is routing to its node, and the node is already a search
  // entry — graph.js indexes every labelled parking node — so the destination
  // is the existing entry rather than a second, parallel notion of one. A rack
  // whose node somehow isn't indexed simply gets no button, which is the same
  // rule the Nearby sheet follows for a place with no door.
  const bikeRoomEntry = useMemo(() => {
    if (!selectedBike || !mapData) return null;
    return (mapData.roomIndex || []).find(
      e => e.parking && (e.nodeIds || []).includes(selectedBike.nodeId)) || null;
  }, [selectedBike, mapData]);

  const handleGoToBike = useCallback(() => {
    if (!bikeRoomEntry) return;
    setSelectedBikeId(null);
    setSelectedRoom(bikeRoomEntry);
    setRouting(true);
    setFromCommitted(!!signPin);
  }, [bikeRoomEntry, signPin]);

  // Which lot's card is open. Held as an ID, not the record: the records are
  // rebuilt every minute against the clock, so keeping the object would freeze
  // an open card at the status it had when it was tapped — and the status is
  // the reason the card exists.
  const [selectedLotId, setSelectedLotId] = useState(null);
  const selectedLot = useMemo(
    () => (selectedLotId ? (parkingLots || []).find(l => l.id === selectedLotId) || null : null),
    [selectedLotId, parkingLots],
  );
  // A lot's card and the room card own the same corner of the screen, so the
  // one being opened wins — the same rule the bus pins follow.
  const handleLotTap = useCallback((id) => {
    setSelectedRoom(null);
    setSelectedBikeId(null);   // one card at a time — they share the same corner
    setSelectedLotId(id);
  }, []);
  // Anything that takes the screen puts the card away: starting a route, or
  // opening the Nearby sheet on top of it. `routing` rather than `route`
  // because the route itself is derived much further down this component — a
  // dependency array is built during render, so naming it here would read it
  // before its own `const`.
  useEffect(() => {
    if (!routing && !placesOpen) return;
    setSelectedLotId(null);
    setSelectedBikeId(null);
  }, [routing, placesOpen]);

  const busPins = useMemo(() => {
    if (!busStops) return null;
    if (!busTier) return busStops;
    const scale = busTier === 3 ? BUS_LABEL_BIG_SCALE : 1;
    const clock = busTier >= 2;
    return busStops.map(s => ({
      ...s,
      scale,
      lines: pinDepartures(busTimes[s.stopKey]?.departures, { now: busNow, clock }),
    }));
  }, [busStops, busTier, busTimes, busNow]);

  // Tapping one opens the stop's own sheet — the same detail view the Nearby
  // panel shows, leading with the next ten departures. Reusing it rather than
  // building a second bus popup means the times, the route badges, the day
  // headings and Directions all behave identically whichever way you arrived.
  //
  // The room card and the route flow are dropped first, for the reason the
  // Nearby/route exclusion effect above exists: they own the same corner of the
  // screen, and `selectedRoom` still being set would close this sheet again the
  // moment it opened.
  const handleBusPin = useCallback((id) => {
    const entry = mapData?.places?.find(e => e.place.id === id);
    if (!entry) return;
    setSelectedRoom(null);
    setRouting(false);
    setPlaceKind('busstop');
    setPlacesOpen(true);
    setSelectedPlace(entry);
    onPlaceLog?.({
      event: 'view', kind: 'busstop', placeId: entry.place.id,
      placeName: placeTitle(entry.place), building: entry.place.building,
    });
  }, [mapData, onPlaceLog]);

  // A shared link opens straight onto that study spot's detail.
  useEffect(() => {
    if (!pendingPlace || !mapData?.places?.length) return;
    const entry = mapData.places.find(e => e.place.id === pendingPlace);
    if (entry) {
      setPlacesOpen(true);
      setSelectedPlace(entry);
    }
    setPendingPlace(null);
  }, [pendingPlace, mapData]);

  // The link that sends someone this exact study spot. Keyed by the place id
  // rather than building+room so a drawn area (which has no room) shares too.
  const placeShareUrl = useMemo(() => {
    if (!selectedPlace) return null;
    try {
      const p = new URLSearchParams({ place: selectedPlace.place.id });
      return `${window.location.origin}${window.location.pathname}?${p.toString()}`;
    } catch { return null; }
  }, [selectedPlace]);

  // Route to a place the same way as any room: find its door in the room index
  // and hand it to the normal destination flow. A place with no matching room
  // entry (a drawn hallway nook, say) has nothing to route to, so the button is
  // hidden for it rather than failing on tap.
  const roomEntryForPlace = useCallback((entry) => {
    const place = entry?.place;
    if (!mapData || !place?.building) return null;

    // An explicitly attached node wins: the authoring tool's "Attach node"
    // states exactly where Directions should land, which is the only answer for
    // a place drawn as an area (no room number at all) and the unambiguous one
    // for a room with several doors. Wrapped in a roomIndex-shaped entry so the
    // rest of the destination flow needs no special case.
    // `routeNodeId` is the bus-stop case: its position is the pole, which is
    // not on the path network, so where it IS and where you walk from are two
    // different points and are stored as two different fields.
    const nodeId = place.navNodeId || place.routeNodeId;
    const node = nodeId && mapData.nodes.get(nodeId);
    if (node) {
      return {
        building: place.building,
        buildingName: entry.buildingName,
        roomNumber: place.roomNumber || placeTitle(place),
        nodeId: node.id,
        nodeIds: [node.id],
      };
    }

    if (!place.roomNumber) return null;
    const room = String(place.roomNumber).toLowerCase();
    return mapData.roomIndex.find(
      r => r.building === place.building && String(r.roomNumber).toLowerCase() === room
    ) || null;
  }, [mapData]);

  const handleGoToPlace = useCallback((entry) => {
    const room = roomEntryForPlace(entry);
    if (!room) return;
    onPlaceLog?.({
      event: 'go', kind: entry.place.type, placeId: entry.place.id,
      placeName: placeTitle(entry.place), building: entry.place.building,
    });
    setPlacesOpen(false);
    setSelectedPlace(null);
    setSelectedRoom(room);
    setRouting(true);
    // Same rule as tapping Go on a searched room: a scanned sign has already
    // answered where you are, so Directions from a Nearby place goes straight to
    // the walk rather than through a form with one filled-in field.
    setFromCommitted(!!signPin);
  }, [roomEntryForPlace, onPlaceLog, signPin]);

  // ── Club events ───────────────────────────────────────────────────────────
  //
  // Pins on the resting map, under the same rule as the bus stops and for a
  // related reason: an event is a thing with an expiry, and somebody crossing
  // campus on the day should not have had to know to go looking for it. Unlike
  // a bus stop the useful fact does not change by the minute, so these are
  // capped at the coming week — a pin for something three months out is not an
  // answer to anything, it is furniture.
  //
  // Everything further out is in the calendar instead, which is the other
  // question ("what is on at all") and belongs in a list, not on a map.
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Where the Events button sits: the measured bottom of the search card.
  //
  // A constant would be wrong the moment the card changes height, and it does —
  // a Course Mode toggle, a From field, a scanned-sign row. Measured, the
  // button rides along with it. The fallback is the card's own resting height,
  // used for the frame or two before the observer has fired.
  const [searchCard, setSearchCard] = useState(null);
  const [searchCardBottom, setSearchCardBottom] = useState(110);
  useEffect(() => {
    if (!searchCard) return undefined;
    const measure = () => setSearchCardBottom(
      Math.round(searchCard.offsetTop + searchCard.offsetHeight + 10));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(searchCard);
    return () => ro.disconnect();
  }, [searchCard]);

  const eventPins = useMemo(() => {
    if (!mapData || !events?.length) return null;
    if (routing || hidePanel || driveRoute || placesOpen) return null;
    const pins = [];
    for (const e of pinnableEvents(events)) {
      const position = eventPosition(mapData, e);
      // No footprint for the building means no honest place to put it. The
      // event is still in the calendar, which is where it can say so in words.
      if (!position) continue;
      pins.push({
        id: e.id, position, logo: e.logo, title: e.name,
        when: e.when?.text || '', tone: e.when?.tone || '',
      });
    }
    return pins.length ? pins : null;
  }, [mapData, events, routing, hidePanel, driveRoute, placesOpen]);

  const selectedEvent = useMemo(
    () => (selectedEventId ? (events || []).find(e => e.id === selectedEventId) || null : null),
    [selectedEventId, events],
  );

  // One thing at a time in the bottom of the screen: the room card, a lot's
  // card and a bike rack's card all live in the same corner, and the calendar
  // is the whole screen.
  const handleEventPin = useCallback((id) => {
    setSelectedRoom(null);
    setSelectedLotId(null);
    setSelectedBikeId(null);
    setPlacesOpen(false);
    setSelectedPlace(null);
    setCalendarOpen(false);
    setSelectedEventId(id);
  }, []);

  // Starting a route takes the sheet away, the same way it takes the room card
  // and the Nearby sheet away — they are all the bottom of the screen, which
  // the route pill and the step directions now own.
  useEffect(() => {
    if (routing || driveRoute) { setSelectedEventId(null); setCalendarOpen(false); }
  }, [routing, driveRoute]);

  const eventDest = useMemo(
    () => (selectedEvent ? eventDestination(mapData, selectedEvent) : null),
    [mapData, selectedEvent],
  );

  const handleGoToEvent = useCallback(() => {
    if (!eventDest) return;
    setSelectedEventId(null);
    setSelectedRoom(eventDest);
    setRouting(true);
    // Same rule as every other Directions button here: a scanned sign has
    // already answered where you are, so this goes straight to the walk rather
    // than through a form with one field already filled in.
    setFromCommitted(!!signPin);
  }, [eventDest, signPin]);

  // From the calendar, which is full-screen: picking a row closes it onto that
  // event's sheet, so the map behind is showing the thing just chosen.
  const handleCalendarPick = useCallback((e) => {
    setCalendarOpen(false);
    handleEventPin(e.id);
  }, [handleEventPin]);

  // Bring the pin into the strip of map the sheet leaves uncovered.
  //
  // A plain panTo centres it on the STAGE, and the bottom two thirds of the
  // stage is the sheet — so the pin somebody just tapped would slide under it.
  // The zoom is deliberately left alone: they tapped a pin they could already
  // see, and flying in on it would answer a question nobody asked.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map || !selectedEvent) return;
    const at = eventPosition(mapData, selectedEvent);
    if (!at) return;
    map.panTo(at);
    // Positive y pans the viewport down, which moves the point UP on screen.
    const h = mapRef.current?.clientHeight || 0;
    if (h) map.panBy(0, Math.round(h * 0.26));
  }, [mapReady, selectedEvent, mapData, mapInstanceRef]);

  /**
   * A destination was chosen in the search box.
   *
   * `meta.via === 'course'` means it came from a course code rather than a room
   * name. That is remembered as THE ENTRY ITSELF rather than a boolean, so it
   * self-clears: every other way of setting a destination (a Nearby place, a
   * bike rack, a shared link) stores a different entry, and the log's check is
   * an identity comparison. A boolean would have to be reset at eight call
   * sites, and the one that got missed would silently attribute somebody's
   * washroom search to Course Mode.
   */
  const [courseDest, setCourseDest] = useState(null);
  const handlePickRoom = useCallback((entry, meta) => {
    setSelectedRoom(entry);
    setCourseDest(meta?.via === 'course' ? entry : null);
  }, []);

  /**
   * Course Mode: the search box searches the term's timetable instead of the
   * room list, and a picked section hands back its room.
   *
   * The timetable is fetched on the FIRST tap of the toggle and kept for the
   * session. It is ~100 KB gzipped of pure text that answers nothing for a
   * visitor looking for a washroom, and the snapshot load is already the
   * slowest moment on this screen — so it does not join it. One fetch, and
   * `courseStatus` is what the dropdown reports while it is in flight or if it
   * never arrives (a host whose snapshot has no courses.json answers null, and
   * that is "unavailable", not an error).
   */
  const [courseMode, setCourseMode] = useState(false);
  const [courseIndex, setCourseIndex] = useState(null);
  const [courseStatus, setCourseStatus] = useState('idle'); // idle | loading | ready | unavailable
  const courseLoadRef = useRef(false);
  const handleCourseMode = useCallback(on => {
    setCourseMode(on);
    if (!on || courseLoadRef.current) return;
    courseLoadRef.current = true;
    if (!ds?.loadCourses) { setCourseStatus('unavailable'); return; }
    setCourseStatus('loading');
    ds.loadCourses()
      .then(json => {
        const idx = buildCourseIndex(json);
        setCourseIndex(idx);
        setCourseStatus(idx ? 'ready' : 'unavailable');
      })
      .catch(() => setCourseStatus('unavailable'));
  }, [ds]);

  const handleClearRoom = useCallback(() => { setSelectedRoom(null); setRouting(false); setRouteConfirmed(false); setFromCommitted(false); }, []);
  /**
   * Open the Directions box — or skip it, when there is nothing to ask.
   *
   * Normally the box shows first so the user can review or enter their starting
   * point. A SCANNED SIGN has already answered that question, and better than
   * the box can: it pins the exact node the sign is bolted next to, where the
   * box can only resolve a room. Showing it anyway means presenting a form whose
   * only field is already filled, and asking the reader to press a button to
   * agree with it.
   *
   * So a scan routes straight through to the pill and the walk. The destination
   * is the one thing the person standing at the sign actually has to supply.
   *
   * A failure still lands in the box — `route` is null, so the panel falls back
   * to it — which is where an error has room to explain itself and where the
   * From field is there to try something else.
   */
  const handleGo = useCallback(() => {
    if (!selectedRoom) return;
    setRouting(true);
    setFromCommitted(!!signPin);
  }, [selectedRoom, signPin]);
  const handleBack = useCallback(() => { setRouting(false); setRouteConfirmed(false); setFromCommitted(false); }, []);
  const handleFindRoute = useCallback(() => setFromCommitted(true), []); // explicit submit from the Directions box
  const handleConfirmRoute = useCallback(() => setRouteConfirmed(true), []);
  // There is no preview stage to go back TO any more, so the nav pill's back
  // button leaves routing entirely rather than stranding the user in a stage
  // the auto-confirm below would immediately walk them out of again.
  const handleUnconfirm = handleBack;
  // Touching the From box drops the pin. Once the text no longer says what the
  // sign said, a hidden node still driving the route is a start the user cannot
  // see, change, or explain — so the pin's lifetime is exactly the lifetime of
  // the text it put there.
  const handleFromText = useCallback(text => {
    fromAutoRef.current = false; setFromCommitted(false); setSignPin(null); setFromText(text);
  }, []);
  const handleClearFrom = useCallback(() => {
    fromAutoRef.current = false; setFromCommitted(false); setSignPin(null); setFromText('');
  }, []);
  const handleFromPick = useCallback(item => {
    fromAutoRef.current = false;
    // Only rooms are pickable as a start — whole buildings aren't routable.
    if (item.kind === 'room') { setFromText(roomLabel(item.entry)); setFromCommitted(true); }
  }, []);

  // Bumped when a building's interior polygons arrive. mapData is mutated in
  // place by attachBuildingInterior — its identity never changes — so anything
  // derived from polygons has to depend on this counter or it will keep serving
  // the answer it computed while the building was still an empty footprint.
  const [interiorsVersion, setInteriorsVersion] = useState(0);
  const interiorReqRef = useRef(new Set());

  // ── Endpoint resolution ────────────────────────────────────────────────────
  const selectedRoomLoc = useMemo(
    () => (mapData && selectedRoom ? getRoomLocation(mapData, selectedRoom) : null),
    // interiorsVersion: the rings only exist once this building's interior has
    // been fetched, so the highlight has to be recomputed when it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapData, selectedRoom, interiorsVersion]
  );

  // Resolve the From text: "My location", "<Building>" (whole building), or
  // "<Building> <room#>". The building name is matched as a prefix so a trailing
  // room number is parsed off the end.
  const fromResolved = useMemo(() => {
    if (!mapData) return { nodeId: null };
    const t = fromText.trim();
    if (!t) return { nodeId: null };
    if (t === MYLOC_LABEL.trim() || /^my location$/i.test(t) || t.startsWith('📍')) {
      if (!userPos) return { nodeId: null, error: 'Waiting for your location…' };
      const hit =
        nearestNode([...mapData.nodes.values()], userPos, { maxMeters: MY_LOCATION_MAX_METERS, filter: n => n.floor === 0 }) ||
        nearestNode([...mapData.nodes.values()], userPos, { maxMeters: MY_LOCATION_MAX_METERS, filter: n => n.type === 'exit' });
      return hit ? { nodeId: hit.node.id } : { nodeId: null, error: 'No walkable path near your location.' };
    }
    // Longest building name that is a prefix of the text
    const lower = t.toLowerCase();
    let building = null;
    for (const b of mapData.buildings) {
      const bn = (b.name || '').toLowerCase();
      if (!bn) continue;
      if (lower === bn || lower.startsWith(bn + ' ')) {
        if (!building || b.name.length > building.name.length) building = b;
      }
    }
    if (building) {
      const rest = t.slice(building.name.length).trim();
      if (!rest) {
        // A whole building is not a routable start — require a specific room.
        return { nodeId: null, error: `Add a room number in ${building.name} — e.g. ${building.name} 200.` };
      }
      const restLower = rest.toLowerCase();
      const entry = mapData.roomIndex.find(r => r.building === building.slug &&
        (r.roomNumber.toLowerCase() === restLower || (r.roomName || '').toLowerCase() === restLower));
      if (entry) return { nodeId: entry.nodeId, nodeIds: entry.nodeIds, entry };
      return { nodeId: null, error: `Room "${rest}" not found in ${building.name}.` };
    }
    // No building prefix — accept an exact "Building Number" label match
    const entry = mapData.roomIndex.find(r => roomLabel(r).toLowerCase() === lower);
    if (entry) return { nodeId: entry.nodeId, nodeIds: entry.nodeIds, entry };
    return { nodeId: null, error: 'Type a building then a room number, or pick from the list.' };
  }, [mapData, fromText, userPos]);

  const toNodeId = selectedRoom?.nodeId || null;

  const fromIsComplete = !!(fromResolved.nodeIds && fromResolved.nodeIds.length);
  // ── Routing — re-runs whenever endpoints or any criterion change ──────────
  // When driven externally the drive props win over the search UI's state.
  // Rooms carry every door node (`nodeIds`) so findRoute settles on the nearest
  // entrance; single-node endpoints (My location, a building origin) pass one.
  // A specific room resolves with door nodes (`nodeIds`); a bare building or "My
  // location" resolves to a single origin node. Only auto-route once From is a
  // specific room — otherwise wait for the explicit "Find Route" tap so the
  // Directions box stays up for the user to add their room number.
  const effRouting = driveRoute ? true : (routing && (fromCommitted || fromIsComplete));
  // An external driver may hand over a single node id or a list of them — a room
  // with four doors is four possible endpoints, and the router settles on the
  // nearest. Wrapping unconditionally would nest the array and match nothing.
  const effFromIds = driveRoute
    ? (Array.isArray(driveRoute.fromId) ? driveRoute.fromId : [driveRoute.fromId])
    // The pin beats the text. The From box shows the sign's label, which is
    // prose ("Drake Centre — north entrance") and resolves to nothing; letting
    // the text win would throw away the exact node the sign is bolted next to.
    : (signPin ? [signPin.nodeId]
      : (fromResolved.nodeIds || (fromResolved.nodeId ? [fromResolved.nodeId] : [])));
  const effToIds = driveRoute
    ? (Array.isArray(driveRoute.toId) ? driveRoute.toId : [driveRoute.toId])
    : (selectedRoom?.nodeIds || (selectedRoom?.nodeId ? [selectedRoom.nodeId] : []));
  const effOptions = driveRoute ? (driveOptions || {}) : options;
  // Stable dep for the memo — the endpoints AND the routing options, by VALUE.
  //
  // The options have to be in here rather than in the dependency array beside
  // it. A driven host that passes no `driveOptions` gets `{}`, and an inline
  // `{}` is a new object on every render, so the memo re-ran every time and
  // handed back a route that was equal in every way except identity. Everything
  // keyed on "this is a new route" then fired on every render — including the
  // effect below that resets to the first leg, which meant tapping a
  // floor-switch popup set the leg and had it reset out from under the tap
  // before the map redrew. Floors could not be changed on any driven map at all.
  // Comparing by value fixes it for every host, memoised or not.
  const effKey = `${effFromIds.join(',')}|${effToIds.join(',')}|${JSON.stringify(effOptions)}`;

  const route = useMemo(() => {
    if (!effRouting || !mapData || effFromIds.length === 0 || effToIds.length === 0) return null;
    return findRoute(mapData, effFromIds, effToIds, tuning ? { ...effOptions, costs: tuning } : effOptions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effRouting, mapData, effKey, tuning]);

  useEffect(() => { setActiveLeg(0); setDirOpen(false); setActiveStepId(null); }, [route]);

  // ── Find Route goes straight into navigating ──────────────────────────────
  // The old flow found the route, showed it whole, and waited for a GO tap that
  // every user makes every time — a confirmation of something they had just
  // asked for. The overview is still worth seeing (it is how you learn the
  // shape of the walk), so it plays as a beat rather than a stage: the camera
  // fits the route, holds it, then flies to the start and navigation begins.
  //
  // `routeConfirmed` survives as the RENDERING distinction between those two
  // camera states; it just is not a decision the user has to make.
  useEffect(() => {
    if (!route || routeConfirmed) return undefined;
    const t = setTimeout(() => setRouteConfirmed(true), ROUTE_OVERVIEW_HOLD_MS);
    return () => clearTimeout(t);
  }, [route, routeConfirmed]);

  // Face the way the walk starts. A vector map (one with a Map ID) can rotate;
  // a raster map cannot, so this is a no-op there rather than a broken view.
  // Only on the first leg of a new route — re-facing on every leg change would
  // spin the map under someone who is reading it.
  const facedRouteRef = useRef(null);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map || !route || !routeConfirmed) return;
    if (facedRouteRef.current === route) return;
    facedRouteRef.current = route;
    if (typeof map.setHeading !== 'function') return;
    const nodes = route.legs[0]?.nodes || route.nodes || [];
    const from = nodes[0];
    // Far enough along to be a direction rather than a rounding error.
    const to = nodes.find(n => n !== from && haversineMeters(from, n) >= 4) || nodes[1];
    if (!from || !to) return;
    try { map.setHeading(bearingDeg(from, to)); } catch { /* raster map */ }
  }, [mapReady, route, routeConfirmed, mapInstanceRef]);

  // Human step-by-step directions for the current route — announces turns at
  // authored intersection nodes (always) and at sharp crossroads otherwise.
  const directions = useMemo(() => buildDirections(route, mapData), [route, mapData]);

  // The room being walked FROM, highlighted like the destination once a route
  // exists and its building's polygons are on screen. Only when From named an
  // actual room — "My location" and a bare building name have no polygon to
  // colour, and the green Start pill already marks the spot in those cases.
  const startRoomLoc = useMemo(
    () => (mapData && route && fromResolved.entry ? getRoomLocation(mapData, fromResolved.entry) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapData, route, fromResolved, interiorsVersion],
  );

  // Tapping a direction (a turn/intersection/entry/shaft/arrival) focuses the map
  // on that spot: switch to the leg/floor that contains it, then pan + zoom in.
  const handleStepClick = useCallback((step) => {
    const map = mapInstanceRef.current;
    if (!map || !step?.at) return;
    setActiveStepId(step.id ?? null);
    if (step.nodeId && route?.legs) {
      const li = route.legs.findIndex(leg => leg.nodes.some(n => n.id === step.nodeId));
      if (li >= 0) setActiveLeg(li);
    }
    map.panTo(step.at);
    map.setZoom(Math.max(map.getZoom() || 0, 19));
  }, [route]);

  // Tapping a turn icon on the map is the same selection the list makes, from
  // the other end: it opens the list if it was collapsed and marks the step, so
  // the two views always agree on which instruction is being looked at, and it
  // centres the map on the turn — the step list slides up over the bottom of
  // the screen, and a turn tapped down there would end up behind it.
  const handleStepIcon = useCallback((step) => {
    setActiveStepId(step.id ?? null);
    setDirOpen(true);
    const map = mapInstanceRef.current;
    if (map && step?.at) map.panTo(step.at);
  }, []);

  /** Route nodes that can be looked at: anything carrying a photo, whatever the
   *  surveyor typed it as. The step list marks these, matching the gold pins on
   *  the map, so every panorama on the route is reachable both ways. */
  const lookNodeIds = useMemo(() => {
    const s = new Set();
    for (const leg of route?.legs || []) {
      for (const n of leg.nodes) if (n.panorama?.image) s.add(n.id);
    }
    return s;
  }, [route]);

  // Tapping such a step goes to the node and opens the panorama, exactly as
  // tapping its pin on the map does — the leg has to be switched first, since
  // the viewer is anchored to an overlay on the shown leg.
  const handleStepLook = useCallback((step) => {
    setActiveStepId(step.id ?? null);
    if (!route?.legs) return;
    for (let li = 0; li < route.legs.length; li++) {
      const nodes = route.legs[li].nodes;
      const i = nodes.findIndex(n => n.id === step.nodeId);
      if (i < 0) continue;
      const node = nodes[i], nxt = nodes[i + 1];
      setActiveLeg(li);
      const map = mapInstanceRef.current;
      if (map) { map.panTo({ lat: node.lat, lng: node.lng }); map.setZoom(Math.max(map.getZoom() || 0, 19)); }
      setPano({ node, bearing: nxt ? bearingDeg(node, nxt) : 0, outNode: nxt });
      return;
    }
  }, [route]);

  // ── Route-search logging (host-provided sink) ─────────────────────────────
  /**
   * One record per DISTINCT search this tab makes — a From→To pair with a set of
   * preferences — so the host can persist usage analytics. Tuner-driven routes
   * are skipped.
   *
   * ⚠️ Every signature is remembered, not just the last one. The single-slot
   * version only stopped an immediate repeat, and the shape of real use is not a
   * straight line: somebody flips a toggle to see what changes, edits the From
   * box and puts it back, goes back to the search screen and returns to the same
   * room. Each of those departs from the last signature and then RETURNS to it,
   * which the old check read as a new search — so one journey landed in the log
   * eight times over seven minutes, and the admin's grouping (which merges only
   * ADJACENT rows) could not put it back together. A Set costs a few dozen
   * strings per tab and makes the count mean journeys again.
   *
   * `courseSig` is in the signature deliberately: walking to a room you found by
   * course code is the same walk, but it is not the same fact about the feature,
   * so both ways of arriving at one room are worth a row.
   */
  const loggedSigsRef = useRef(new Set());
  useEffect(() => {
    if (!onRouteLog || driveRoute) return;
    if (!routing || effFromIds.length === 0 || effToIds.length === 0) return;
    const o = effOptions;
    const viaCourse = !!courseDest && courseDest === selectedRoom;
    const sig = `${effKey}|${o.accessible ? 1 : 0}${o.tunnels ? 1 : 0}${o.noElevators ? 1 : 0}${o.noTunnels ? 1 : 0}${viaCourse ? 'c' : ''}`;
    if (loggedSigsRef.current.has(sig)) return;
    loggedSigsRef.current.add(sig);
    onRouteLog({
      from: { text: fromText.trim(), nodeId: signPin?.nodeId || fromResolved.nodeId || null },
      // Which printed sign this search started from, if any — the only way to
      // learn whether the signage is being used, and by how many. `scan` is the
      // sign's printed IDENTIFIER where the link carried one, because that is
      // what names a sign on a wall and what the scan totals group by; the node
      // it resolved to rides alongside rather than replacing it.
      scan: signPin?.code || signPin?.nodeId || null,
      scanNode: signPin?.nodeId || null,
      scanBuilding: signPin ? (mapData?.nodes.get(signPin.nodeId)?.building || null) : null,
      to: selectedRoom
        ? { building: selectedRoom.building, room: selectedRoom.roomNumber, name: selectedRoom.buildingName, nodeId: selectedRoom.nodeId }
        : null,
      // `course` is not a routing preference — it says the destination was found
      // by course code in Course Mode rather than typed as a room. It rides with
      // the options because that is the one thing about a search the log already
      // counts per day, which is what makes "how many people used it" answerable
      // after the rolling sample has scrolled past.
      options: {
        accessible: !!o.accessible, tunnels: !!o.tunnels,
        noElevators: !!o.noElevators, noTunnels: !!o.noTunnels,
        course: viaCourse,
      },
      found: !!route,
      // WHY a search came back empty, in the same words the status line uses.
      // "No route" and "no route that avoids stairs" are different facts about
      // a building, and only the second one is an accessibility finding.
      reason: route ? null : failureReason(o, effFromIds, effToIds),
      meters: route ? Math.round(route.totalMeters) : null,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routing, effKey, effOptions, route, onRouteLog, driveRoute, courseDest, selectedRoom]);

  const status = useMemo(() => {
    if (dataError) return { kind: 'error', text: `Map data failed to load: ${dataError}` };
    if (!routing) return { kind: 'idle' };
    // A scanned sign IS a start, even with nothing typed in the From box.
    if (!fromText.trim() && !signPin) return { kind: 'idle' };
    if (fromResolved.error) return { kind: 'error', text: fromResolved.error };
    if (route) return { kind: 'ok' };
    if (effRouting && mapData && fromResolved.nodeId && toNodeId) {
      if (fromResolved.nodeId === toNodeId) return { kind: 'idle' };
      if (options.accessible && options.noElevators)
        return { kind: 'error', text: 'No route with both Accessible and No elevators on — floors can\'t be changed. Try allowing elevators.' };
      if (options.accessible)
        return { kind: 'error', text: 'No accessible route found.' };
      return { kind: 'error', text: 'No route found.' };
    }
    return { kind: 'idle' };
  }, [dataError, routing, effRouting, fromText, signPin, fromResolved, route, mapData, toNodeId, options]);

  // ── Map layers ─────────────────────────────────────────────────────────────
  const roomInfo = useMemo(() => {
    if (!selectedRoom) return null;
    const roomType = selectedRoom.roomType || null; // from the reference spreadsheet
    // With a friendly name: name on top, "Building Number" gray below.
    if (selectedRoom.roomName) {
      return {
        title: selectedRoom.roomName,
        subtitle: selectedRoom.roomNumber
          ? `${selectedRoom.buildingName} ${selectedRoom.roomNumber}`
          : selectedRoom.buildingName,
        roomType,
      };
    }
    return {
      title: selectedRoom.buildingName,
      subtitle: selectedRoom.roomNumber ? `Room ${selectedRoom.roomNumber}` : '',
      roomType,
    };
  }, [selectedRoom]);
  // Short display strings for the route start/end pill markers. A driven route
  // has no search boxes to read the answer off, so a host-supplied name wins.
  const fromLabel = driveFromLabel || fromText.trim().replace(/^📍\s*/, '')
    || signPin?.place || 'Start';
  const toLabel = driveToLabel || (selectedRoom
    ? `${selectedRoom.buildingName} ${selectedRoom.roomNumber}`.trim()
    : 'End');

  // A shareable deep link that reopens this route on another device (Share menu).
  const shareUrl = useMemo(() => {
    if (!selectedRoom) return null;
    try {
      const p = new URLSearchParams();
      const f = fromText.trim();
      if (f) p.set('from', f);
      p.set('b', selectedRoom.building);
      p.set('r', selectedRoom.roomNumber);
      // Only when set, so an ordinary route still shares the short link it
      // always did. Sharing "the accessible way to Wallace 305" and having it
      // open as the stairs route would be the same bug as reopening a rating
      // without them.
      if (accessibleMode) p.set('a', String(accessibleMode));
      if (tunnelMode) p.set('t', String(tunnelMode));
      return `${window.location.origin}${window.location.pathname}?${p.toString()}`;
    } catch { return null; }
  }, [fromText, selectedRoom, accessibleMode, tunnelMode]);

  // Attach a snapshot of the current session to a problem report, then hand it
  // to the host to persist. Captures From/To, options, whether a route was
  // found, the current stage, and device/UA — nothing personal.
  const handleReportSubmit = useCallback((message) => {
    const stage = routeConfirmed ? 'navigation'
      : route ? 'preview'
      : routing ? 'directions'
      : selectedRoom ? 'room-selected' : 'search';
    onReport?.({
      message,
      session: {
        from: fromText.trim(),
        to: selectedRoom
          ? { building: selectedRoom.building, room: selectedRoom.roomNumber, name: selectedRoom.buildingName }
          : null,
        options: {
          accessible: !!options.accessible, tunnels: !!options.tunnels,
          noElevators: !!options.noElevators, noTunnels: !!options.noTunnels,
        },
        routeFound: !!route,
        meters: route ? Math.round(route.totalMeters) : null,
        stage,
        device: deviceLabel(),
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        url: window.location.href,
      },
    });
  }, [onReport, routeConfirmed, route, routing, selectedRoom, fromText, options]);

  // Must be identity-stable: the route layer rebuilds its polylines, chevron
  // timer, "Look" pins and floor-switch overlays whenever this changes, so an
  // inline arrow here would throw all of that away on every unrelated render.
  const handleIntersection = useCallback(
    (node, bearing, outNode) => setPano({ node, bearing, outNode }), []);

  // ── Interior polygons, on demand ──────────────────────────────────────────
  // Buildings arrive as footprints. Fetch the inside of one only once the user
  // has given a reason to see it — a route through it, a room picked in it, a
  // place previewed in it. Everything degrades rather than breaks while a fetch
  // is in flight: search reads the nav graph, and a room with no polygon yet
  // falls back to its door node, so nothing waits on this.

  const needInteriors = useMemo(() => {
    const want = new Set();
    for (const leg of route?.legs || []) for (const n of leg.nodes) if (n.building) want.add(n.building);
    if (selectedRoom?.building) want.add(selectedRoom.building);
    if (selectedPlace?.loc?.building) want.add(selectedPlace.loc.building);
    // A scanned sign is the strongest reason of the lot to fetch a building's
    // insides: somebody is standing in it, holding a phone, before they have
    // searched for anything at all.
    if (scanLoc?.building) want.add(scanLoc.building);
    return [...want];
  }, [route, selectedRoom, selectedPlace, scanLoc]);

  useEffect(() => {
    if (!mapData || !ds?.loadBuilding) return;
    let cancelled = false;
    const pending = needInteriors.filter(slug => {
      const b = mapData.buildings.find(x => x.slug === slug);
      return b && b.outlineOnly && !interiorReqRef.current.has(slug);
    });
    if (!pending.length) return;
    pending.forEach(slug => interiorReqRef.current.add(slug));
    Promise.all(pending.map(slug =>
      ds.loadBuilding(slug)
        .then(json => (json && attachBuildingInterior(mapData, slug, json)))
        // A failed fetch un-marks the building so a later attempt can retry.
        .catch(() => { interiorReqRef.current.delete(slug); return false; })
    )).then(results => {
      if (!cancelled && results.some(Boolean)) setInteriorsVersion(v => v + 1);
    });
    return () => { cancelled = true; };
  }, [mapData, ds, needInteriors]);

  useRouteLayer({
    interiorsVersion,
    mapReady, mapInstanceRef, mapData, route, activeLeg, setActiveLeg, userPos,
    routeConfirmed,
    fromLabel, toLabel,
    selectedRoom: selectedRoomLoc,
    startRoom: startRoomLoc,
    scanLoc,
    placeLoc: selectedPlace?.loc || null,
    placePins,
    overviewPins,
    busPins,
    bikePins,
    eventPins,
    parkingLots,
    onPlacePin: handlePlacePin,
    onBusPin: handleBusPin,
    onBikePin: handleBikePin,
    onEventPin: handleEventPin,
    onLot: handleLotTap,
    fitPinsKey,
    roomInfo,
    showRoomInfo: !!selectedRoom && !routing,
    onGoRoom: handleGo,
    onCloseRoom: handleClearRoom,
    onIntersection: handleIntersection,
    directions,
    activeStepId,
    onStepIcon: handleStepIcon,
  });

  // Derive the panorama's heading + fov (and the exact GO fraction) from the
  // intersection's per-edge correspondences: each stored edge fraction pairs with
  // that neighbour's real bearing, so the photo is stretched to match the map.
  const panoCal = useMemo(() => {
    if (!pano || !mapData) return null;
    const p = pano.node.panorama || {};
    const kind = p.kind || 'flat';
    const pairs = [];
    for (const e of p.edges || []) {
      const nb = mapData.nodes.get(e.to);
      if (nb && typeof e.frac === 'number') pairs.push({ bearing: bearingDeg(pano.node, nb), frac: e.frac });
    }
    const fit = fitPanoHeadingFov(pairs, { defaultFov: kind === 'equirect' ? 360 : 180 });
    const heading = fit ? fit.heading : (p.heading ?? 0);
    const fov = fit ? fit.fov : (p.fov ?? (kind === 'equirect' ? 360 : 180));
    const outEdge = pano.outNode && (p.edges || []).find(e => e.to === pano.outNode.id && typeof e.frac === 'number');
    return { heading, fov, exitFrac: outEdge ? outEdge.frac : null };
  }, [pano, mapData]);

  // View cone at the open intersection — a small translucent-gold sector pointing
  // the way the panorama viewer is looking, so panning the photo swings the cone.
  //
  // Redrawing it is a `setPath` on a polygon we already own, so it runs straight
  // off the viewer's callback without going through React at all. `panoRef`
  // mirrors the open intersection so this stays identity-stable and the viewer
  // never has to be re-rendered to keep the cone in sync.
  const panoRef = useRef(null);
  useEffect(() => { panoRef.current = pano; }, [pano]);

  const drawCone = useCallback(() => {
    const p = panoRef.current;
    const poly = coneRef.current;
    if (!p || !poly) return;
    const view = panoViewRef.current;
    const node = p.node;
    const bearing = view?.bearing ?? p.bearing;                 // default: looking toward the exit
    const half = Math.max(6, Math.min(45, (view?.fov ?? 45) / 2));
    const radius = 13; // metres — small
    const pts = [{ lat: node.lat, lng: node.lng }];
    const step = Math.max(3, half / 6);
    for (let a = -half; a <= half + 0.001; a += step) pts.push(destinationPoint(node, bearing + a, radius));
    poly.setPath(pts);
  }, []);

  const handlePanoView = useCallback((bearing, fovVisible) => {
    panoViewRef.current = { bearing, fov: fovVisible };
    drawCone();
  }, [drawCone]);
  const handlePanoClose = useCallback(() => setPano(null), []);

  // The window stays up until it is dismissed or the map is moved — nothing
  // else takes it away. Moving the map is a dismissal because the window is
  // pinned to its node: pan or zoom and it slides off with it, so treating that
  // as "I'm done here" beats leaving a window tethered to somewhere off screen.
  //
  // Opening the window pans and zooms to the node itself, so these listeners
  // can only be attached once that has settled — otherwise the window would
  // close itself the instant it opened. `idle` marks the end of the programmatic
  // move; the timer is a backstop for when the map was already there and never
  // moves, in which case no `idle` is coming.
  //
  // Dragging the PHOTO must not count. It doesn't: PanoramaViewer stops the
  // map-gesture events on the window, so no drag ever reaches the map.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map || !pano) return;
    const gmaps = window.google.maps;
    const close = () => setPano(null);
    let live = [];
    let armed = false;
    const arm = () => {
      if (armed) return;
      armed = true;
      live = ['dragstart', 'zoom_changed', 'heading_changed', 'tilt_changed']
        .map(ev => gmaps.event.addListener(map, ev, close));
    };
    const once = gmaps.event.addListenerOnce(map, 'idle', arm);
    const timer = setTimeout(arm, 700);
    return () => {
      clearTimeout(timer);
      gmaps.event.removeListener(once);
      live.forEach(l => gmaps.event.removeListener(l));
    };
  }, [mapReady, pano, mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map) return;
    if (!pano) {
      panoViewRef.current = null;
      if (coneRef.current) { coneRef.current.setMap(null); coneRef.current = null; }
      return;
    }
    // A different intersection starts from its own default heading, not wherever
    // the last photo happened to be left pointing.
    panoViewRef.current = null;
    if (!coneRef.current) {
      coneRef.current = new window.google.maps.Polygon({
        map, clickable: false, zIndex: 610,
        fillColor: '#fcb40c', fillOpacity: 0.3, strokeColor: '#fcb40c', strokeOpacity: 0.85, strokeWeight: 1,
      });
    }
    drawCone();
  }, [mapReady, pano, drawCone, mapInstanceRef]);
  useEffect(() => () => { if (coneRef.current) { coneRef.current.setMap(null); coneRef.current = null; } }, []);

  // Anchor the panorama window above the node: centre the map on it, then track
  // its screen position with an OverlayView and portal the viewer into that div,
  // so the window floats over the node and the cone stays visible around it.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map || !pano) { setPanoDiv(null); return; }
    const gmaps = window.google.maps;
    const latLng = new gmaps.LatLng(pano.node.lat, pano.node.lng);
    map.panTo(latLng);
    let div = null;
    class Anchor extends gmaps.OverlayView {
      onAdd() { div = document.createElement('div'); div.className = 'pfa-pano-anchor'; this.getPanes().floatPane.appendChild(div); setPanoDiv(div); }
      draw() { const p = this.getProjection()?.fromLatLngToDivPixel(latLng); if (p && div) { div.style.left = `${p.x}px`; div.style.top = `${p.y}px`; } }
      onRemove() { div?.remove(); div = null; }
    }
    const o = new Anchor(); o.setMap(map);
    return () => { o.setMap(null); setPanoDiv(null); };
  }, [mapReady, pano]);

  if (loadError) return <div className="pfa-root"><div className="pfa-fatal">Google Maps failed to load.</div></div>;

  return (
    <div className={`pfa-root${notice || signNotice ? ' pfa-has-notice' : ''}`}>
      <div className="pfa-map" ref={mapRef} />
      {/* A scanned sign that didn't resolve takes the strip over: it is about
          this visit specifically, and the standing disclaimer is not. Tapping
          dismisses, because after the first read it is just a bar in the way. */}
      {signNotice
        ? <div className="pfa-notice pfa-notice-qr" onClick={() => setSignNotice('')}>{signNotice}</div>
        : notice && <div className="pfa-notice">{notice}</div>}
      {!isLoaded && <div className="pfa-fatal">Loading map…</div>}
      {/* onRouteLog is host-owned; PathfinderTab only builds and hands off the record. */}
      {/* The Nearby sheet takes over the screen while it is open: the search
          card behind it answers a different question, and hiding it gives the
          sheet the whole map to grow into rather than a strip below the card. */}
      {mapData && !hidePanel && !placesOpen && (
        <SearchPanel
          buildings={mapData.buildings}
          roomIndex={mapData.roomIndex}
          routing={routing}
          selectedRoom={selectedRoom}
          onPickRoom={handlePickRoom}
          onClearRoom={handleClearRoom}
          onGo={handleGo}
          onBack={handleBack}
          onFindRoute={handleFindRoute}
          fromText={fromText}
          startPin={signPin}
          onFromTextChange={handleFromText}
          onFromPick={handleFromPick}
          onClearFrom={handleClearFrom}
          accessibleMode={accessibleMode}
          onAccessibleMode={setAccessibleMode}
          tunnelMode={tunnelMode}
          onTunnelMode={setTunnelMode}
          status={status}
          route={route}
          activeLeg={activeLeg}
          routeConfirmed={routeConfirmed}
          onConfirmRoute={handleConfirmRoute}
          onUnconfirm={handleUnconfirm}
          walkSpeed={tuning?.walkSpeed}
          shareUrl={shareUrl}
          renderQr={renderQr}
          onOpenSupport={() => setSupportOpen(true)}
          onOpenReport={() => setReportOpen(true)}
          courseMode={courseMode}
          onCourseMode={handleCourseMode}
          courseIndex={courseIndex}
          courseStatus={courseStatus}
          cardRef={setSearchCard}
        />
      )}
      {/* Below the search card and detached from it, because it opens a
          different thing: the card answers "where is this room", this answers
          "what is on". Its top is the measured bottom of the card, so it
          follows the card as that grows a Course Mode toggle or a From field —
          and it sits BEHIND the card (z-index 19), so a long list of search
          results simply covers it rather than shunting it off the screen. */}
      {events?.length > 0 && !hidePanel && !routing && !placesOpen && !calendarOpen && (
        <div className="pfa-underpanel" style={{ top: searchCardBottom }}>
          <button className="pfa-events-btn" onClick={() => setCalendarOpen(true)}>
            <CalendarMark />
            <span>Events</span>
          </button>
        </div>
      )}
      {/* Step-by-step directions — sits above the route pill, expands upward. */}
      {mapData && !hidePanel && route && (
        <DirectionsSteps
          steps={directions}
          open={dirOpen}
          onToggle={() => setDirOpen(o => !o)}
          onStepClick={handleStepClick}
          onStepLook={handleStepLook}
          lookNodeIds={lookNodeIds}
          activeStepId={activeStepId}
          destLabel={selectedRoom ? toLabel : null}
        />
      )}
      {/* Study spots, libraries and bus stops. Draws nothing until the host
          opens it — a tapped bus-stop pin, or a shared ?place= link. */}
      {mapData?.places?.length > 0 && !hidePanel && !routing && (
        <PlacesPanel
          entries={mapData.places}
          origin={userPos}
          imageUrl={ds?.imageUrl}
          open={placesOpen}
          onClose={handleClosePlaces}
          selected={selectedPlace}
          onSelect={handleSelectPlace}
          onGo={selectedPlace && roomEntryForPlace(selectedPlace) ? handleGoToPlace : null}
          onPhotoSubmit={onPhotoSubmit}
          loadDepartures={ds?.loadDepartures}
          ratings={placeRatings}
          onRate={onPlaceRate ? handlePlaceRate : null}
          shareUrl={placeShareUrl}
          kind={placeKind}
          onKind={handlePlaceKind}
          filters={placeFilters}
          onFilters={setPlaceFilters}
          onLog={onPlaceLog}
        />
      )}
      {/* A tapped parking lot or bike rack — the same card either way. Hidden
          with the rest of the furniture when a host is driving the map. */}
      {selectedLot && !hidePanel && (
        <ParkingLotCard lot={selectedLot} onClose={() => setSelectedLotId(null)} />
      )}
      {selectedBike && !hidePanel && (
        <BikeParkingCard spot={selectedBike} onClose={() => setSelectedBikeId(null)}
                         onGo={bikeRoomEntry ? handleGoToBike : null} />
      )}
      {/* A tapped event pin, or a row picked out of the calendar. */}
      {selectedEvent && !hidePanel && (
        <EventSheet
          event={selectedEvent}
          onClose={() => setSelectedEventId(null)}
          onGo={eventDest ? handleGoToEvent : null}
          renderBody={renderEventBody}
        />
      )}
      {calendarOpen && !hidePanel && (
        <EventCalendar events={events || []} onPick={handleCalendarPick}
                       onClose={() => setCalendarOpen(false)} clubLogin={clubLogin} />
      )}
      {/* The bottom-centre slot the "Nearby" button used to hold. Everything
          that button opened — the places, the filters, the photos — is what the
          phone app is for, so this map's job at the end of a search is to hand
          the reader over to it. Hidden whenever anything else owns the bottom
          of the screen, which is the same rule the button before it followed. */}
      {(renderAppPromo || appPromo?.href) && !hidePanel && !routing && !placesOpen && !selectedEvent
        && !calendarOpen && !selectedLot && !selectedBike && (renderAppPromo ? (
        <div className="pfa-app-slot">{renderAppPromo()}</div>
      ) : (
        <a className="pfa-app-fab" href={appPromo.href} target="_blank" rel="noopener noreferrer">
          {appPromo.icon && <img src={appPromo.icon} alt="" />}
          <span>
            <b>{appPromo.title || 'Get the app'}</b>
            {appPromo.sub && <i>{appPromo.sub}</i>}
          </span>
          <em aria-hidden="true">›</em>
        </a>
      ))}
      {dataError && !mapData && <div className="pfa-fatal">{dataError}</div>}
      {/* The one-time popup. When the host has given a `support` ask it is
          THAT — see the note on the prop — and otherwise the older welcome.
          ⚠️ The support version passes no `onEmail`, which is what takes the
          email box away: the first visit now asks for one thing. Addresses are
          still collected, every visit, behind the "?" below. */}
      {feedbackOpen && (
        support?.blurb ? (
          <SupportPopup
            title={support.title || 'Please support UMaps'}
            blurb={support.blurb}
            donateHref={support.href}
            onClose={dismissFeedback}
          />
        ) : (
          <SupportPopup
            title="Welcome to UMaps"
            // The disclaimer used to open this sentence; it is now a line of its
            // own inside the popup, shared with the "?" — one wording, one place.
            blurb={
              'Follow us on Instagram, or leave your umanitoba email below ' +
              'to get updates and help drive where the app goes next!'
            }
            emailPrompt={null}
            onEmail={submitWelcomeEmail}
            donateHref={support?.href}
            onClose={dismissFeedback}
          />
        )
      )}
      {/* The "?" keeps both: how to reach us, the email box, and — since
          somebody who opened this is already asking about the project — the
          same donation link. */}
      {supportOpen && (
        <SupportPopup onEmail={onEmailCollect} donateHref={support?.href}
                      onClose={() => setSupportOpen(false)} />
      )}
      {reportOpen && <ReportPopup onSubmit={handleReportSubmit} onClose={() => setReportOpen(false)} />}
      {pano && panoDiv && createPortal(
        <PanoramaViewer
          imageUrl={ds?.imageUrl ? ds.imageUrl(pano.node.panorama.image, 'pano') : pano.node.panorama.image}
          kind={pano.node.panorama.kind || 'flat'}
          heading={panoCal?.heading ?? 0}
          fov={panoCal?.fov}
          exitFrac={panoCal?.exitFrac}
          targetBearing={pano.bearing}
          onView={handlePanoView}
          onClose={handlePanoClose}
        />,
        panoDiv
      )}
    </div>
  );
}

/**
 * The mark on the Events button. Drawn rather than an emoji, matching the rest
 * of this app's icons (see components/PlaceIcons.jsx) — an emoji calendar is a
 * different picture on every platform and carries a date nobody chose.
 */
function CalendarMark() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.2" y="5" width="17.6" height="16" rx="2.6" />
      <path d="M3.2 9.6h17.6M8 3v3.4M16 3v3.4" />
    </svg>
  );
}
